// [TODO] - Not sure if anything is using this Row Variant at the moment
import React from 'react';
import { StyleSheet } from 'react-native';
import { DocumentStatus, FileType } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNACard, DNAText, DNAButton, Iffy, useTheme } from '@alucio/lux-ui';
import {
  DNAChip,
  DNACustomFieldChipList,
  DNADocumentStatusChip,
  DNADocumentThumbnail,
  DNADocumentVersionContextMenu,
  DNADocumentVersionUpdateButton,
  DNARowsComponent,
  useCurrentPage,
  useDNADocumentVersionActions,
} from 'src/components/DNA';
import { DocumentVersionORM, FolderItemORM } from 'src/types/types';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import { CustomLabelDisplay } from './CustomLabelDisplay';

export type DNADocumentVersionDefaultRowProps = {
  isOnline?: boolean,
}

const styles = StyleSheet.create({
  bookmarkButtonPWAWrapper: {
    marginRight: 10,
    paddingRight: 30,
    width: 22,
  },
});

const DNADocumentFolderItemDefaultRow: DNARowsComponent<
  FolderItemORM,
  DNADocumentVersionDefaultRowProps
> = (props) => {
  const {
    ORM: folderItem,
    onPress,
    style,
    isDesktop = true,
    isOnline,
    variant,
  } = props
  const hideControls = variant === 'dashboardFavorites'
  const theme = useTheme()
  // [TODO] - I think we need to revisit this? A Folder Item should not be limited to a DocVerORM
  /** TODO: Refactor improper use of as assertion */
  const documentVersionORM = (folderItem.relations.itemORM as DocumentVersionORM)
  const isBookmarked = documentVersionORM.relations.documentORM.meta.bookmark.isBookmarked
  const documentVerActions = useDNADocumentVersionActions()
  const route = useCurrentPage({ exact: false })
  const { status: docStatus } = documentVersionORM.relations.documentORM.model
  const { visiblePages } = folderItem.model
  const isModified =
    visiblePages?.length &&
    documentVersionORM.meta.permissions.MSLSelectSlides &&
    documentVersionORM.model.status === 'PUBLISHED'
  const isLatestPublished = documentVersionORM.meta.version.isLatestPublished
  // [TODO-ORM] - We should have a property that combines isOnline and isContentCached
  //               on the DocumentVersionORM rather than checking both at the component level
  const unavailableContent = !isOnline && (!documentVersionORM.meta.assets.isContentCached ||
    documentVersionORM.model.type === FileType.WEB);

  const canPresent = documentVersionORM.relations.documentORM.meta.permissions.MSLPresent
  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const handleOnPress = () => {
    documentVerActions.present(documentVersionORM, folderItem)()
  }

  // If the publisher's selected cover page was hidden by the user
  // we use the first visible page
  // [TODO-2126] - We should be able calculate this at the ORM level instead
  let customThumbnailKey: string | undefined
  const selectedThumbnail = documentVersionORM.model.selectedThumbnail ?? 1
  if (folderItem.model.visiblePages && !folderItem.model.visiblePages.find(vp => vp === selectedThumbnail)) {
    const customPage = documentVersionORM.meta.allPages[folderItem.model.visiblePages[0] - 1]
    customThumbnailKey = detectArchivedFileKeyPath(documentVersionORM.model, customPage)
  }

  return (
    <DNACard
      appearance="flat"
      interactive="pressable"
      onPress={() => {
        !unavailableContent && onPress?.(folderItem);
      }}
      style={[
        {
          paddingHorizontal: theme['row-padding-horizontal'],
          paddingVertical: theme['row-padding-vertical'],
        },
        style,
      ]}
    >
      <DNABox
        testID="folder-item-row"
        alignY="center"
        spacing={isDesktop ? 'between' : 'md'}
        childFill={1}
        style={{ opacity: unavailableContent ? 0.4 : 1 }}
      >
        <Iffy is={!isDesktop}>
          <DNABox spacing="md">
            <Iffy is={!hideControls}>
              <DNABox
                style={styles.bookmarkButtonPWAWrapper}
              >
                <Iffy is={isLatestPublished}>
                  <DNAButton
                    appearance="ghost"
                    status={isBookmarked ? 'primary' : 'subtle'}
                    size={isDesktop ? 'md' : 'xl'}
                    iconLeft={isBookmarked ? 'star-fill' : 'star'}
                    onPress={documentVerActions.bookmark(documentVersionORM)}
                  />
                </Iffy>
              </DNABox>
            </Iffy>
            <Iffy is={!hideControls}>
              <DNADocumentVersionContextMenu
                folderItemORM={folderItem}
                unavailableContent={unavailableContent}
              >
                <DNAButton
                  testID="folder-item-ellipsis"
                  appearance="ghost"
                  status="subtle"
                  size={isDesktop ? 'md' : 'xl'}
                  iconLeft="dots-three-vertical-bold"
                />
              </DNADocumentVersionContextMenu>
            </Iffy>
          </DNABox>
        </Iffy>
        <DNABox
          spacing="lg"
          alignY="center"
          childFill={1}
        >
          <DNADocumentThumbnail.Hover
            documentVersionORM={documentVersionORM}
            width={120}
            height={68}
            unavailableContent={unavailableContent}
            customThumbnailKey={customThumbnailKey}
            onPress={canPresent ? handleOnPress : undefined}
          />
          {/* Title */}
          <DNABox
            spacing="sm"
            appearance="col"
            childFill
            style={{ paddingRight: 16 }}
          >
            <DNAText testID="document-item-title" b1 numberOfLines={1}>
              {folderItem.meta.title}
            </DNAText>
            <DNABox
              alignY="center"
              spacing="sm"
              fill
              childStyle={[4, { flex: 1 }]}
            >
              <DNABox appearance="row">
                <DNACustomFieldChipList documentVersionORM={documentVersionORM} />
                <Iffy is={isPublisherRoute}>
                  <DNABox style={{ marginHorizontal: 8 }}>
                    <DNADocumentStatusChip
                      documentStatus={documentVersionORM.relations.documentORM.model.status}
                      isScheduled={documentVersionORM.meta.schedule.publish.isScheduled}
                      isDraft={
                        documentVersionORM.relations.documentORM.meta.hasUnpublishedVersion &&
                        !documentVersionORM.meta.schedule.publish.isScheduled
                      }
                    />
                  </DNABox>
                </Iffy>
              </DNABox>
              <Iffy is={docStatus === DocumentStatus.ARCHIVED}>
                <DNADocumentStatusChip documentStatus={DocumentStatus.ARCHIVED} />
              </Iffy>
              <Iffy is={isModified}>
                <DNAChip.Beacon variant="MODIFIED" />
              </Iffy>
              <Iffy is={unavailableContent}>
                <DNAChip.Beacon
                  testID={`content-not-available-${folderItem.meta.title}`}
                  variant="CONTENT_UNAVAILABLE"
                />
              </Iffy>
              <CustomLabelDisplay
                documentORM={documentVersionORM.relations.documentORM}
              />
            </DNABox>
          </DNABox>
        </DNABox>
        {/* Actions */}
        <Iffy is={isDesktop}>
          <DNABox>
            <DNADocumentVersionUpdateButton orm={folderItem} />
            {/* Bookmark Button */}
            <Iffy is={!hideControls}>
              <Iffy is={isLatestPublished && !isPublisherRoute}>
                <DNAButton
                  appearance="ghost"
                  status={isBookmarked ? 'primary' : 'subtle'}
                  size={isDesktop ? 'md' : 'xl'}
                  onPress={documentVerActions.bookmark(documentVersionORM)}
                  iconLeft={isBookmarked ? 'star-fill' : 'star'}
                />
              </Iffy>
            </Iffy>
            <Iffy is={!hideControls}>
              <DNADocumentVersionContextMenu
                folderItemORM={folderItem}
                unavailableContent={unavailableContent}
              >
                <DNAButton
                  appearance="ghost"
                  status="subtle"
                  size={isDesktop ? 'md' : 'xl'}
                  iconLeft="dots-three-vertical-bold"
                />
              </DNADocumentVersionContextMenu>
            </Iffy>
          </DNABox>
        </Iffy>
        <Iffy is={!isDesktop}>
          {/* Update Version Notification */}
          <DNADocumentVersionUpdateButton orm={folderItem} />
        </Iffy>
      </DNABox>
    </DNACard>
  );
}

export default DNADocumentFolderItemDefaultRow
